import FileApi from '@apis/file/FileApi';
import useLoading from '@hooks/ui/useLoading';
import useToast from '@hooks/ui/useToast';
import { PresignedUrlResponse } from '@models/file';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import {
    ChangeEvent, Dispatch, SetStateAction, useEffect,
} from 'react';

interface Props {
    value?: string[],
    setValue?: Dispatch<SetStateAction<string[]>>,
    preview: PresignedUrlResponse[],
    setPreview: Dispatch<SetStateAction<PresignedUrlResponse[]>>;
    maxLength?: number;
}

function useUploadFile({
    value, setValue, preview, setPreview, maxLength,
}:Props) {
    const convertFileToWebp = async (file: File): Promise<File> => await imageCompression(file, {
        maxSizeMB: 2,
        useWebWorker: true,
        fileType: 'image/webp',
    });
    const toast = useToast();
    const { startLoading, stopLoading } = useLoading();

    const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const { files } = e.target;
        try {
            if (files) {
                if (maxLength && maxLength < files.length + (value?.length || 0)) {
                    toast.error(`최대 ${maxLength}개까지 첨부 가능합니다.`);
                    return;
                }
                startLoading();
                for (let i = 0; i < files.length; i += 1) {
                    const file = files[i];
                    const convertedFile = await convertFileToWebp(file);
                    const res = await FileApi.issuePresignedUrl({
                        type: 'INQUIRY',
                        extension: 'webp',
                        fileName: file.name,
                        fileSize: convertedFile.size,
                    });
                    await axios.put(res.presignedUrl, convertedFile, {
                        headers: {
                            'Content-Type': 'image/webp',
                        },
                    });
                    setPreview((prev) => [...prev, res]);
                    setValue?.((prev) => [...prev, res.tempFileKey]);
                }
            }
        } catch (error) {
            toast.error('파일 업로드에 실패했습니다.');
            console.error(error);
        }
        stopLoading();
    };

    const removeFile = (tempFileKey: string) => {
        setPreview((prev) => prev.filter((p) => p.tempFileKey !== tempFileKey));
        setValue?.((prev) => prev?.filter((v) => v !== tempFileKey));
    };

    useEffect(() => {
        if (value && value.length === 0) {
            setPreview([]);
        }
    }, [value]);

    return {
        onFileChange,
        removeFile,
    };
}

export default useUploadFile;
